/**
 * StaffRosterView.tsx
 * The standalone "Roster" Phase-2 view mode (Mike simplify PR2, MIKE-SIMPLIFY.md
 * D2 + PR2). This is the "just the roster" surface Mike asked for: every person
 * on the job in a clean, full-width people×weeks timeline — their assigned tasks
 * as faint context + ONE editable blanket % per person·week (default 100,
 * over-100 nags, dial to 0 to take them off the job that week), a per-week team
 * total, and an add-person row.
 *
 * It is the SECOND entry point into the SHARED roster timeline built in PR1: the
 * "Staff this job" hero embeds the same `StaffRosterTimeline` as its capacity
 * hero, and this mode renders it on its own (no task-staffing cards). Both wire
 * the SAME `useStaffJobModel` (one roster + one capacity fetch + one skill
 * snapshot), so the two entry points always show identical, consistent data +
 * editing — one roster-timeline component, two callers (DRY).
 *
 * 2026-07-14 (user feedback): the compact task×week "Task Weekly Schedule" table
 * they liked (image 33) is restored as a TOP section here — the SAME
 * `Phase2WeeklyGrid` Classic renders, on its own shared-rail scroll container +
 * sticky week header — with the people×weeks roster timeline stacked below it.
 *
 * Colors via theme tokens — no hex literals (CODING-STANDARDS).
 */

import React, { useMemo } from 'react';
import { Alert, Box, Divider, Snackbar, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { useTheme } from '@mui/material/styles';

import { StaffRosterTimeline } from './StaffRosterTimeline';
import { Phase2RosterGrid } from './Phase2RosterGrid';
import { Phase2RailHeader } from './Phase2RailHeader';
import { useStaffJobModel } from './useStaffJobModel';
import { weekStartsInRange } from '../../../../utils/weekUtils';
import type { Scenario } from '../../../../services/scheduleScenario';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';

export interface StaffRosterViewProps {
  scenario: Scenario;
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  scenarioKey: string | null;
  tasks: TaskWithBookings[];
  assignmentMap: Record<string, TaskAssignee[]>;
  /** Plan is Submitted/Archived → read-only. */
  locked: boolean;
  onAllocationChanged: () => void;
  /** Reload one task's assignees after a presence write. */
  onTaskPresenceChanged: (taskKey: string) => void;
  capacityRefreshKey: number;
  hoveredWeek: string | null;
  onHoverWeek: (weekStart: string | null) => void;
  /** ISO stat-holiday dates — forwarded to the task grid's booking day-strips. */
  holidays?: readonly string[];
}

/** BST week-start Saturdays spanning all tasks (matches Phase2WeeklyGrid's own span). */
function computeWeekColumns(tasks: TaskWithBookings[]): string[] {
  let minStart: string | null = null;
  let maxEnd: string | null = null;
  for (const t of tasks) {
    if (t.StartDate && (!minStart || t.StartDate < minStart)) minStart = t.StartDate;
    if (t.EndDate && (!maxEnd || t.EndDate > maxEnd)) maxEnd = t.EndDate;
  }
  return minStart && maxEnd ? weekStartsInRange(minStart, maxEnd) : [];
}

export const StaffRosterView: React.FC<StaffRosterViewProps> = ({
  scenario,
  source,
  sourceKey,
  scenarioKey,
  tasks,
  assignmentMap,
  locked,
  onAllocationChanged,
  onTaskPresenceChanged,
  capacityRefreshKey,
  hoveredWeek,
  onHoverWeek,
  holidays,
}) => {
  const theme = useTheme();
  void scenario; // kept in the props for parity with the Staff hero entry point

  const model = useStaffJobModel({
    source,
    sourceKey,
    scenarioKey,
    tasks,
    assignmentMap,
    locked,
    onTaskPresenceChanged,
    onAllocationChanged,
    capacityRefreshKey,
  });

  // Derive the rail week columns from the tasks (identically to Phase2WeeklyGrid),
  // so the sticky header and the grid always agree on the span.
  const railWeekColumns = useMemo(() => computeWeekColumns(tasks), [tasks]);
  const holidayList = useMemo(() => (holidays ? [...holidays] : []), [holidays]);
  // A scheduled/archived plan is read-only — grey the editable grid out (parity
  // with Classic's locked body treatment).
  const lockedBodySx = locked
    ? { opacity: 0.55, pointerEvents: 'none' as const, filter: 'grayscale(0.25)' }
    : {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {/* ── The ORIGINAL roster "Task Weekly Schedule" table (booking lanes with
          day chips + photo-avatar people, Phase2RosterGrid) — restored on TOP,
          above the people timeline, on its own rail with a sticky week header. ── */}
      {tasks.length > 0 && railWeekColumns.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
            <PrecisionManufacturingIcon sx={{ fontSize: 18, color: theme.schedule.machineTime }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
              Task Weekly Schedule (Machine Time + Employee Allocation)
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
          {/* ONE horizontal scroll container so the sticky name column pins on the
              left and the week header rides above the grid, week-for-week. */}
          <Box
            onMouseLeave={() => onHoverWeek(null)}
            sx={{ overflowX: 'auto', maxWidth: '100%', scrollbarWidth: 'thin', ...lockedBodySx }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', width: 'fit-content' }}>
              <Phase2RailHeader
                weekColumns={railWeekColumns}
                holidays={holidayList}
                hoveredWeek={hoveredWeek}
                onHoverWeek={onHoverWeek}
              />
              <Phase2RosterGrid
                tasks={tasks}
                assignmentMap={assignmentMap}
                scenarioKey={scenarioKey}
                onAssignmentsChanged={onTaskPresenceChanged}
                onAllocationChanged={onAllocationChanged}
                compareToBaseline={false}
                hoveredWeek={hoveredWeek}
                onHoverWeek={onHoverWeek}
                holidays={holidayList}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Framing — this is the whole roster, not per-task. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupsIcon sx={{ fontSize: 18, color: theme.schedule.employeeAlloc }} />
        <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: '0.9rem' }}>
          Roster — everyone on the job, week by week
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          Set how much of each person&rsquo;s week this {source === 'Quote' ? 'quote' : 'work order'} takes. Over 100% is allowed (advisory).
        </Typography>
      </Box>
      <Divider />

      {/* The SHARED people×weeks roster timeline (same component the Staff hero uses). */}
      <StaffRosterTimeline
        model={model}
        tasks={tasks}
        assignmentMap={assignmentMap}
        editable={!locked}
        hoveredWeek={hoveredWeek}
        onHoverWeek={onHoverWeek}
        source={source}
        sourceKey={sourceKey}
        scenarioKey={scenarioKey}
      />

      {/* Roster (%/add/remove) mutation error — verbatim. */}
      <Snackbar
        open={Boolean(model.rosterMutationError)}
        autoHideDuration={7000}
        onClose={model.clearRosterMutationError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={model.clearRosterMutationError} sx={{ width: '100%' }}>
          {model.rosterMutationError}
        </Alert>
      </Snackbar>

      {/* Task-write error (day-off collision / lock / network) — verbatim. */}
      <Snackbar
        open={Boolean(model.error)}
        autoHideDuration={7000}
        onClose={model.clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={model.clearError} sx={{ width: '100%' }}>
          {model.error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
